// pdf.js loading/rendering plus the mapping between PDF user space and screen
// pixels. Every conversion in the app funnels through here so rotated pages and
// offset CropBoxes are handled in exactly one place.

// The *legacy* build is deliberate: pdf.js v6's modern bundle assumes very new
// engine features (e.g. `Map.prototype.getOrInsertComputed`) that current
// browsers do not all ship yet. The legacy build carries the polyfills, so the
// page works for ordinary visitors rather than only the newest Chrome.
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';
import workerUrl from 'pdfjs-dist/legacy/build/pdf.worker.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;

export const OPS = pdfjsLib.OPS;

export async function loadDocument(bytes) {
  return pdfjsLib.getDocument({
    // The copy is required, not defensive: pdf.js transfers this buffer to its
    // worker, which detaches the original. Passing the app's own copy would
    // leave nothing to hand pdf-lib at export time.
    data: new Uint8Array(bytes),
    isEvalSupported: false,
  }).promise;
}

/** Render a page into a canvas at `scale` and return it with its viewport. */
export async function renderPage(page, scale) {
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  const ratio = window.devicePixelRatio || 1;
  canvas.width = Math.floor(viewport.width * ratio);
  canvas.height = Math.floor(viewport.height * ratio);
  canvas.style.width = `${viewport.width}px`;
  canvas.style.height = `${viewport.height}px`;

  const context = canvas.getContext('2d');
  context.scale(ratio, ratio);
  await page.render({ canvasContext: context, viewport }).promise;
  return { canvas, viewport };
}

/**
 * PDF user-space rect -> CSS pixel rect within the page container.
 * Corners are round-tripped through the viewport so page rotation is applied.
 */
export function rectToScreen(rect, viewport) {
  const [ax, ay] = viewport.convertToViewportPoint(rect.x, rect.y);
  const [bx, by] = viewport.convertToViewportPoint(
    rect.x + rect.width,
    rect.y + rect.height,
  );
  return {
    left: Math.min(ax, bx),
    top: Math.min(ay, by),
    width: Math.abs(bx - ax),
    height: Math.abs(by - ay),
  };
}

/** CSS pixel rect -> PDF user-space rect. */
export function screenToRect(screen, viewport) {
  const [ax, ay] = viewport.convertToPdfPoint(screen.left, screen.top);
  const [bx, by] = viewport.convertToPdfPoint(
    screen.left + screen.width,
    screen.top + screen.height,
  );
  return {
    x: Math.min(ax, bx),
    y: Math.min(ay, by),
    width: Math.abs(bx - ax),
    height: Math.abs(by - ay),
  };
}
